<template>
  <div class="deliverable-dependencies">
    <!-- ── Linked Deliverables ── -->
    <div class="dep-section mb-4">
      <div class="is-flex is-align-items-center is-justify-content-space-between mb-2">
        <p class="is-size-7 has-text-weight-semibold has-text-grey">
          <i class="fa-solid fa-link mr-1"></i>Linked
        </p>
        <button
          v-if="!readonly"
          class="button is-small is-primary is-outlined"
          @click="showLinkPicker = !showLinkPicker"
        >
          <span class="icon is-small"><i class="fa-solid fa-plus"></i></span>
          <span>Link</span>
        </button>
      </div>

      <div v-if="showLinkPicker" class="add-dep-row mb-3">
        <div class="add-dep-select">
          <SearchableSelect
            :items="availableForLinking"
            :model-value="selectedLinkIds"
            value-field="id"
            display-field="title"
            placeholder="Select deliverables to link..."
            :multiple="true"
            size="small"
            @update:modelValue="selectedLinkIds = $event"
          />
        </div>
        <button
          class="button is-small is-primary"
          :disabled="selectedLinkIds.length === 0"
          @click="addLinks"
        >
          Link
        </button>
      </div>

      <div v-if="enrichedLinks.length > 0" class="dep-list">
        <div v-for="link in enrichedLinks" :key="link.id" class="dep-item">
          <span class="dep-title dep-clickable" @click="$emit('navigate', link.id)">{{ link.title }}</span>
          <button
            v-if="!readonly"
            class="delete is-small ml-auto"
            @click="removeLink(link.id)"
          ></button>
        </div>
      </div>
      <p v-else class="is-size-7 has-text-grey-light">No linked deliverables</p>
    </div>

    <!-- ── Dependencies ── -->
    <div class="dep-section">
      <div class="is-flex is-align-items-center is-justify-content-space-between mb-2">
        <p class="is-size-7 has-text-weight-semibold has-text-grey">
          <i class="fa-solid fa-arrow-right-to-bracket mr-1"></i>Dependencies
        </p>
        <button
          v-if="!readonly"
          class="button is-small is-primary is-outlined"
          @click="showDepPicker = !showDepPicker"
        >
          <span class="icon is-small"><i class="fa-solid fa-plus"></i></span>
          <span>Add</span>
        </button>
      </div>

      <div v-if="showDepPicker" class="add-dep-row mb-3">
        <div class="add-dep-select">
          <SearchableSelect
            :items="availableForDeps"
            :model-value="selectedDepIds"
            value-field="id"
            display-field="title"
            placeholder="Select deliverables..."
            :multiple="true"
            size="small"
            @update:modelValue="selectedDepIds = $event"
          />
        </div>
        <div class="add-dep-type">
          <div class="select is-small">
            <select v-model="newDepType">
              <option value="review">Review</option>
              <option value="start">Start</option>
            </select>
          </div>
        </div>
        <button
          class="button is-small is-primary"
          :disabled="selectedDepIds.length === 0"
          @click="addDependencies"
        >
          Add
        </button>
      </div>

      <div v-if="dependencies.length > 0" class="dep-list">
        <div v-for="dep in enrichedDependencies" :key="dep.deliverableId" class="dep-item">
          <span class="tag is-small mr-2" :class="dep.type === 'review' ? 'is-link' : 'is-info'">
            {{ dep.type }}
          </span>
          <!-- clickable title -->
          <span class="dep-title dep-clickable" @click="$emit('navigate', dep.deliverableId)">{{ dep.title }}</span>
          <span class="tag is-small ml-2" :class="dep.met ? 'is-success' : 'is-warning'">
            {{ dep.met ? 'met' : 'pending' }}
          </span>
          <button
            v-if="!readonly"
            class="delete is-small ml-auto"
            @click="removeDependency(dep.deliverableId)"
          ></button>
        </div>
      </div>
      <p v-else class="is-size-7 has-text-grey-light">No dependencies</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { deliverableService } from '@/services/deliverableService'
import { DEPENDENCY_TYPES } from '@/utils/deliverableUtils'
import { logger } from '@/services/loggerService'
import SearchableSelect from '@/components/common/SearchableSelect.vue'

const props = defineProps({
  deliverableId: { type: String, required: true },
  projectId: { type: String, required: true },
  dependencies: { type: Array, default: () => [] },
  linkedDeliverableIds: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
})

const emit = defineEmits(['update:dependencies', 'update:linkedDeliverableIds', 'navigate'])

// Link picker state
const showLinkPicker = ref(false)
const selectedLinkIds = ref([])

// Dependency picker state
const showDepPicker = ref(false)
const selectedDepIds = ref([])
const newDepType = ref('review')

// Shared data
const siblings = ref([])

async function loadSiblings() {
  try {
    const result = await deliverableService.getProjectDeliverables(props.projectId)
    siblings.value = result.filter(d => d.id !== props.deliverableId)
  } catch (err) {
    logger.warn('[DeliverableDependencies] Failed to load siblings', { error: err.message })
  }
}

// ── Linked ──

const availableForLinking = computed(() => {
  const existingIds = new Set(props.linkedDeliverableIds)
  return siblings.value.filter(s => !existingIds.has(s.id))
})

const enrichedLinks = computed(() => {
  return props.linkedDeliverableIds.map(id => {
    const sib = siblings.value.find(s => s.id === id)
    return { id, title: sib?.title || id }
  })
})

function addLinks() {
  if (selectedLinkIds.value.length === 0) return
  const updated = [...new Set([...props.linkedDeliverableIds, ...selectedLinkIds.value])]
  emit('update:linkedDeliverableIds', updated)
  selectedLinkIds.value = []
  showLinkPicker.value = false
}

function removeLink(id) {
  const updated = props.linkedDeliverableIds.filter(lid => lid !== id)
  emit('update:linkedDeliverableIds', updated)
}

// ── Dependencies ──

const availableForDeps = computed(() => {
  const existingIds = new Set(props.dependencies.map(d => d.deliverableId))
  return siblings.value.filter(s => !existingIds.has(s.id))
})

const enrichedDependencies = computed(() => {
  return props.dependencies.map(dep => {
    const sib = siblings.value.find(s => s.id === dep.deliverableId)
    const stage = sib?.stage || 'unknown'
    let met = true
    if (dep.type === DEPENDENCY_TYPES.START) {
      met = stage !== 'not_ready'
    } else if (dep.type === DEPENDENCY_TYPES.REVIEW) {
      met = ['approved', 'complete'].includes(stage)
    }
    return {
      ...dep,
      title: sib?.title || dep.deliverableId,
      currentStage: stage,
      met,
    }
  })
})

function addDependencies() {
  if (selectedDepIds.value.length === 0) return
  const newDeps = selectedDepIds.value.map(id => ({ deliverableId: id, type: newDepType.value }))
  const updated = [...props.dependencies, ...newDeps]
  emit('update:dependencies', updated)
  selectedDepIds.value = []
  showDepPicker.value = false
}

function removeDependency(depId) {
  const updated = props.dependencies.filter(d => d.deliverableId !== depId)
  emit('update:dependencies', updated)
}

onMounted(() => {
  loadSiblings()
})

watch(() => props.projectId, () => {
  loadSiblings()
})
</script>

<style scoped>
.deliverable-dependencies {
  padding: 0.5rem 0;
}

.dep-section + .dep-section {
  border-top: 1px solid hsl(0, 0%, 93%);
  padding-top: 0.75rem;
}

.add-dep-row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}

.add-dep-row .button {
  align-self: stretch;
}

.add-dep-select {
  flex: 1;
  min-width: 0;
}

.add-dep-type {
  flex-shrink: 0;
  display: flex;
}

.add-dep-type :deep(.select) {
  height: 100%;
  display: flex;
}

.add-dep-type :deep(select) {
  height: 100%;
}

.dep-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.dep-item {
  display: flex;
  align-items: center;
  padding: 0.3rem 0.5rem;
  background: hsl(0, 0%, 97%);
  border-radius: 4px;
  font-size: 0.8rem;
}

.dep-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.dep-clickable {
  cursor: pointer;
  color: hsl(217, 71%, 53%);
}

.dep-clickable:hover {
  text-decoration: underline;
}
</style>
